import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../../services/api';
import { clearMustChangePassword, logout } from '../../store/slices/authSlice';

export default function ChangePasswordRequired() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((s) => s.auth);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError(t('changePasswordRequired.mismatch'));
      return;
    }
    if (newPassword === currentPassword) {
      setError(t('changePasswordRequired.sameAsOld'));
      return;
    }
    setSaving(true);
    try {
      await authAPI.changePassword({ currentPassword, newPassword });
      dispatch(clearMustChangePassword());
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err?.response?.data?.error || t('changePasswordRequired.failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '24px', background: '#f5f6f8',
    }}>
      <div style={{
        width: '100%', maxWidth: 440, background: '#fff', borderRadius: 16,
        padding: '32px 32px 28px', boxShadow: '0 10px 40px rgba(15,23,42,0.08)',
        border: '1px solid #eceef2',
      }}>
        <div style={{
          width: 44, height: 44, borderRadius: 12, background: '#6366f115',
          display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20,
        }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </div>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#0b1220' }}>
          {t('changePasswordRequired.title')}
        </h1>
        <p style={{ margin: '8px 0 24px', fontSize: 14, lineHeight: 1.6, color: '#5b6472' }}>
          {t('changePasswordRequired.subtitle', { email: user?.email || '' })}
        </p>

        {error && (
          <div style={{
            padding: '10px 14px', marginBottom: 16, borderRadius: 10,
            background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c',
            fontSize: 13,
          }}>{error}</div>
        )}

        <form onSubmit={handleSubmit}>
          <Field label={t('changePasswordRequired.currentPassword')}
            value={currentPassword} onChange={setCurrentPassword}
            autoComplete="current-password" autoFocus />
          <Field label={t('changePasswordRequired.newPassword')}
            value={newPassword} onChange={setNewPassword}
            autoComplete="new-password"
            hint={t('changePasswordRequired.rules')} />
          <Field label={t('changePasswordRequired.confirmPassword')}
            value={confirmPassword} onChange={setConfirmPassword}
            autoComplete="new-password" />

          <button type="submit" disabled={saving}
            style={{
              width: '100%', padding: '12px 16px', marginTop: 8, borderRadius: 10,
              background: '#6366f1', color: '#fff', border: 'none', cursor: 'pointer',
              fontSize: 14, fontWeight: 600,
              opacity: saving ? 0.7 : 1,
            }}>
            {saving ? t('changePasswordRequired.saving') : t('changePasswordRequired.submit')}
          </button>
        </form>

        <button type="button" onClick={() => dispatch(logout())}
          style={{
            marginTop: 14, width: '100%', padding: '10px', borderRadius: 10,
            background: 'transparent', border: 'none', color: '#64748b',
            fontSize: 13, cursor: 'pointer',
          }}>
          {t('changePasswordRequired.signOut')}
        </button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, autoComplete, hint, autoFocus }) {
  return (
    <label style={{ display: 'block', marginBottom: 14 }}>
      <span style={{
        display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 600,
        color: '#374151',
      }}>{label}</span>
      <input
        type="password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        required
        style={{
          width: '100%', padding: '10px 12px', borderRadius: 10,
          border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box',
          outline: 'none',
        }}
      />
      {hint && (
        <span style={{ display: 'block', marginTop: 6, fontSize: 12, color: '#8b94a3' }}>{hint}</span>
      )}
    </label>
  );
}
